import { createApi } from "../../../../utils/createApi";
import { AxiosError, AxiosResponse } from "axios";
import { universalAPIErrorHandler } from "../../../../utils/universalAPIErrorHandler";
import { useSitesPageStore } from "../sitesPageStore/sitesPageStore";
import { logger } from "../../../../../utils/logger";

/** Sites per request; the first page renders on its own (ENG-2634). */
export const SITES_PAGE_SIZE = 100;
/** Background pages in flight at once. */
export const SITES_PAGE_CONCURRENCY = 3;

/** Bumped on every load, so a newer load (even of the same group) wins. */
let loadSeq = 0;

/**
 * Stop a background group load from writing rows: call before anything else
 * replaces the Sites table's rows (route change, bulk-action refresh).
 */
export const cancelGroupSitesLoad = () => {
  loadSeq += 1;
  useSitesPageStore.getState().setGroupSitesProgress(null);
};

export const getIndividualGroup = async (
  id: any,
  setRenderedRows: any,
  setPageLoading: any,
  setSiteBreadcrumbs: any,
  setSelected: any,
  setEditableGroupSelected: any,
  setSelectedGroupListName: any,
  navigate: any
) => {
  setPageLoading(true);
  const AxiosUI = createApi(`customer/`);
  const Axios = createApi(``);
  const retryFunction = () => {
    getIndividualGroup(
      id,
      setRenderedRows,
      setPageLoading,
      setSiteBreadcrumbs,
      setSelected,
      setEditableGroupSelected,
      setSelectedGroupListName,
      navigate
    );
  };
  // localStorage.setItem("group_id", id);
  localStorage.setItem("selectedGroupOrSiteId", id);
  setSelected([]);
  useSitesPageStore.getState().setGroupSitesProgress(null);

  // Still the latest load, for the group the user asked for? Earlier clicks
  // may still be loading.
  const thisLoad = ++loadSeq;
  const stillCurrent = () =>
    thisLoad === loadSeq && id === useSitesPageStore.getState().searchGroupId;
  const { setGroupSitesProgress } = useSitesPageStore.getState();

  const loadBreadcrumbs = () =>
    Axios.get(`group/${id}/ancestors/`).then(
      (response: AxiosResponse) => {
        if (Number(id) === Number(localStorage.getIndividualGroupId)) {
          setSiteBreadcrumbs(response.data);
        }
        setPageLoading(false);
      },
      (reason: AxiosError) => {
        universalAPIErrorHandler(reason, navigate, retryFunction, () => {
          setSiteBreadcrumbs([]);
          setPageLoading(false);
        });
      }
    );

  /**
   * ENG-2634: a group with thousands of sites took ~2 min to show anything
   * because every 1000-site page (15-40 ms per site server-side) was fetched
   * in sequence before rendering. Render the first SITES_PAGE_SIZE sites as
   * soon as they arrive, then fetch the rest a few pages at a time in the
   * background, adding rows as they come in. groupSitesProgress tells the
   * table the list is still partial (progress note, select-all off).
   */
  const getGroups = async () => {
    const fetchPage = (pageNumber: number) =>
      AxiosUI.get(
        `/by_group_id/${id}/?page=${pageNumber}&page_size=${SITES_PAGE_SIZE}`
      );

    let first: AxiosResponse;
    try {
      first = await fetchPage(1);
    } catch (reason) {
      universalAPIErrorHandler(
        reason as AxiosError,
        navigate,
        retryFunction,
        () => {
          setPageLoading(false);
        }
      );
      return;
    }
    if (!stillCurrent()) return;

    const pages: any[][] = [first.data.results ?? []];
    const total: number = first.data.count ?? pages[0].length;
    const lastPage = Math.max(1, Math.ceil(total / SITES_PAGE_SIZE));
    const loadedRows = () =>
      pages.reduce(
        (rows: any[], page) => (page ? rows.concat(page) : rows),
        []
      );

    setRenderedRows(loadedRows());
    setGroupSitesProgress(
      lastPage > 1
        ? {
            groupId: id,
            loaded: pages[0].length,
            total,
            done: false,
            failed: false,
          }
        : null
    );
    loadBreadcrumbs();
    if (lastPage === 1) return;

    let nextPage = 2;
    let failed = false;
    const worker = async () => {
      while (nextPage <= lastPage) {
        const pageNumber = nextPage++;
        if (!stillCurrent()) return;
        try {
          const response = await fetchPage(pageNumber);
          if (!stillCurrent()) return;
          pages[pageNumber - 1] = response.data.results ?? [];
        } catch (reason) {
          failed = true;
          logger.error(
            `Sites page: page ${pageNumber} of group ${id} failed to load`,
            reason
          );
        }
        if (!stillCurrent()) return;
        const rows = loadedRows();
        setRenderedRows(rows);
        setGroupSitesProgress({
          groupId: id,
          loaded: rows.length,
          total,
          done: false,
          failed,
        });
      }
    };
    await Promise.all(
      Array.from(
        { length: Math.min(SITES_PAGE_CONCURRENCY, lastPage - 1) },
        worker
      )
    );
    if (!stillCurrent()) return;
    const rows = loadedRows();
    setGroupSitesProgress(
      failed
        ? { groupId: id, loaded: rows.length, total, done: true, failed: true }
        : null
    );
  };

  await Axios.get(`/group/${id}/`).then(
    (response: AxiosResponse) => {
      const searchGroupId = useSitesPageStore.getState().searchGroupId;
      if (id === searchGroupId) {
        localStorage.setItem(
          "selectedIndvidualGroupInfo",
          JSON.stringify(response.data)
        );
        if (response.data.parent === null) {
          setEditableGroupSelected(false);
          setSelectedGroupListName(response.data.name);
        }
      }
    },
    (reason: AxiosError) => {
      universalAPIErrorHandler(reason, navigate, retryFunction, () => {});
    }
  );

  getGroups();
};
